import { Component, OnInit, inject, signal } from '@angular/core';
import { Location } from '@angular/common';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxMaskPipe } from 'ngx-mask';

import { Types } from '../../../domain/types.domain';
import { User } from '../../../domain/user/entities/user.entity';
import { InputComponent, LoadingBarComponent } from '../../../@ads/components';
import { AuthRepositoryInstruction } from '../../../builder/instructions/repositories/auth.repository.instructions';

import { AppHandler } from '../../app.handler';
import { RedirectService } from '../../redirect.service';
import { PageTitleComponent } from '../../components/abstracts/page-title.component';
import { OidcContinuation, postAuthContinuation } from '../../utils/govbr-authorize';
import { AccountFormHandler, AddressFormValue } from '../create-account/account-form.handler';
import { AddressFormComponent } from '../create-account/components/address-form/address-form.component';

export interface RegistrationCompletionState {
  registrationToken: string;
  account: User;
  continuation?: OidcContinuation;
}

const NO_NUMBER = 'S/N';

type CompletionError = 'expired' | 'unavailable' | 'saveFailed';

const ERRORS: Record<CompletionError, { message: string; description: string; actionText: string }> = {
  expired: {
    message: 'O tempo para completar o cadastro acabou.',
    description: 'Entre novamente para continuar.',
    actionText: 'Voltar ao login',
  },
  unavailable: {
    message: 'Não foi possível abrir o cadastro.',
    description: 'Entre novamente para continuar.',
    actionText: 'Voltar ao login',
  },
  saveFailed: {
    message: 'Não foi possível salvar o cadastro.',
    description: 'Se o problema persistir, entre em contato com nossa equipe de suporte.',
    actionText: 'Tentar novamente',
  },
};

@Component({
  selector: 'app-registration-completion',
  standalone: true,
  imports: [ReactiveFormsModule, NgxMaskPipe, InputComponent, LoadingBarComponent, AddressFormComponent],
  providers: [AccountFormHandler, new AuthRepositoryInstruction()],
  templateUrl: './registration-completion.component.html',
  styleUrls: ['../create-account-pj/create-account-pj.component.scss', './registration-completion.component.scss'],
})
export class RegistrationCompletionComponent extends PageTitleComponent('Completar cadastro') implements OnInit {
  private readonly _router = inject(Router);
  private readonly _location = inject(Location);
  private readonly _route = inject(ActivatedRoute);
  private readonly _appHandler = inject(AppHandler);
  private readonly _redirectService = inject(RedirectService);
  private readonly _accountFormHandler = inject(AccountFormHandler);
  private readonly _authRepository = inject(Types.AuthRepository);

  private readonly _state = this._router.getCurrentNavigation()?.extras?.state as
    | RegistrationCompletionState
    | undefined;

  private _entry?: RegistrationCompletionState;

  public readonly account = signal<User | undefined>(undefined);
  public readonly phoneNumber = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern(/^\d{10,11}$/)],
  });
  public addressValid = signal(false);
  public saving = signal(false);

  get isCompany(): boolean {
    return this.account()?.type === 'business';
  }

  private get city() {
    return this._route.snapshot.data['city'];
  }

  async ngOnInit(): Promise<void> {
    this._appHandler.setBackButtonHome(false);

    let entry: RegistrationCompletionState | undefined;
    try {
      entry = this._state ?? (await this.entryFromLink());
    } catch (errorResponse: any) {
      this._appHandler.setLoading(false);
      this.showError(isExpired(errorResponse) ? 'expired' : 'unavailable');
      return;
    }

    this._appHandler.setLoading(false);
    if (!entry?.registrationToken || !entry.account) {
      this.backToLogin();
      return;
    }

    this.open(entry);
  }

  async submit(): Promise<void> {
    if (this.saving() || !this.addressValid() || this.phoneNumber.invalid) return;

    const address = this._accountFormHandler.getAddressInfo() ?? {};
    this.saving.set(true);
    try {
      const { code, redirectUri } = await this._authRepository.completeRegistration({
        registrationToken: this._entry!.registrationToken,
        phoneNumber: this.phoneNumber.value,
        termsAccepted: true,
        address: {
          cep: address.cep ?? '',
          rua: address.rua ?? '',
          numero: address.numero || NO_NUMBER,
          complemento: address.complemento || undefined,
          bairro: address.bairro ?? '',
          cidade: address.cidade ?? '',
          estado: address.estado ?? '',
        },
      });
      this._redirectService.postAuth({ redirectUri, code, ...postAuthContinuation(this._entry!.continuation ?? {}) });
    } catch (errorResponse: any) {
      this.saving.set(false);
      this.showError(isExpired(errorResponse) ? 'expired' : 'saveFailed');
    }
  }

  backToLogin(): void {
    if (this.city?.loginUrl) {
      window.location.href = this.city.loginUrl;
      return;
    }

    this._router.navigate([`/${this.city?.identidade}`]);
  }

  /** A troca de conta, no aprova-frontend, chega por link com o token no fragmento. */
  private async entryFromLink(): Promise<RegistrationCompletionState | undefined> {
    const registrationToken = new URLSearchParams(this._route.snapshot.fragment ?? '').get('token');
    if (!registrationToken) return undefined;

    this._location.replaceState(this._location.path());
    this._appHandler.setLoading(true);
    const account = await this._authRepository.getRegistrationCompletionAccount(registrationToken);
    return { registrationToken, account };
  }

  private open(entry: RegistrationCompletionState): void {
    this._entry = entry;
    this.phoneNumber.setValue(entry.account.userMetadata?.phoneNumber ?? '');
    this._accountFormHandler.setAddressInfo(addressOf(entry.account));
    this.account.set(entry.account);
  }

  private showError(error: CompletionError): void {
    this._appHandler.setError(true, {
      ...ERRORS[error],
      action: () => {
        this._appHandler.clearError();
        if (error !== 'saveFailed') this.backToLogin();
      },
    });
  }
}

function isExpired(errorResponse: any): boolean {
  return errorResponse?.error?.code === 'registrationCompletionExpired';
}

function addressOf({ userMetadata = {} }: User): AddressFormValue {
  const noNumberAddress = userMetadata.numero === NO_NUMBER;
  return {
    cep: userMetadata.cep ?? '',
    rua: userMetadata.rua ?? '',
    numero: noNumberAddress ? '' : userMetadata.numero ?? '',
    complemento: userMetadata.complemento ?? '',
    bairro: userMetadata.bairro ?? '',
    cidade: userMetadata.cidade ?? '',
    estado: userMetadata.estado ?? '',
    noNumberAddress,
  };
}
